/**
 * Shared guide detail-panel renderer + right-panel payload builder.
 *
 * ONE renderer for the guide-detail panel body (loading / empty / happy-path
 * sections), extracted from `InsightsSection.renderGuideDetailPanel` /
 * `renderGuideSection` — mirrors `partnerNotesDrillHelper.js`'s shape (itself
 * created one commit prior for the identical reason: a detail-panel renderer
 * gaining a second real consumer). `InsightsSection.js` (existing consumer,
 * `/library` Guides tab) and `SessionsSection.js` (new consumer, `/search`)
 * both import this module — no duplicate implementation.
 *
 * `buildGuideDetailPayload` mirrors `buildNoteDetailPayload`'s shape: it takes
 * semantic callbacks (`onStar`, `onOption`) and builds the
 * `starButton`/`optionsDropdown` objects internally from those callbacks + the
 * guide's own state (`!!guide.starredAt`, etc.) — callers never pre-build UI
 * objects themselves. It assumes `guide` is a fully-loaded
 * raw `UserGuide` row (has `.content`, `.starredAt`, `.archivedAt`) — used
 * only by `SessionsSection`, which awaits the fetch before ever opening the
 * panel. `InsightsSection` has its own timing landmine (`right-panel:open` is
 * emitted BEFORE its guide fetch resolves, so `this.selectedGuide` is briefly
 * null) and therefore builds its own header payload directly, using
 * `renderGuideDetailBody` only for the body content.
 *
 * The `contentType` is always the literal `'library-guide'` — the exact
 * string `InsightsSection.js` already emits — so a panel opened from either
 * surface is recognized as closeable by whichever section's
 * `right-panel:closed` listener is currently active.
 *
 * @module components/guideDrillHelper
 */

import { escapeHtml, eventBus, isNewSinceViewed } from '../core/utils.js';
import { expandDisclosure } from './disclosure.js';
import { renderMarkdown } from '../utils/markdownRenderer.js';
import { API } from '../core/api.js';
import { buildGuideManagementDropdown, buildGuideOptionsDropdown } from './guideOptionsHelper.js';
import { launchPracticeBuild } from '../orchestration/practiceBuildLauncher.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';
import { iconArrowUpRight } from '../utils/icons.js';
import { formatLongDate } from './partnerNotesDrillHelper.js';
import { GUIDE_PART_TOGETHER, GUIDE_PART_YOU } from './guidePartMark.js';
import { discOverlaysHtml } from './pathDisc.js';
import {
  hasRenderableSections,
  isFlooredOutWaiting,
  renderTogetherColophon,
  wireEnableToggle
} from './sharedGuideDrillHelper.js';

/** Right-panel `contentType` shared by both guide-detail consumers. */
export const GUIDE_DETAIL_CONTENT_TYPE = 'library-guide';

/**
 * Blueprint-declared section content types (mirrors `GuideContentTypes` in
 * `backend/src/content/guides/types.js`). One discriminator for the data shape,
 * the renderer branch, and the per-atom chat affordance.
 */
export const GuideContentTypes = {
  DOORWAY: 'doorway',
  INSIGHT: 'insight',
  ACTION: 'action',
  THREAD: 'thread'
};

/**
 * Resolve a section's presentation contract.
 *
 * This is the ONE place a section's `type` slug may be inspected, and only for
 * rows generated before `contentType` was stamped at persistence. Everything
 * downstream — the renderer here and the per-atom buttons — consumes the
 * resolved contract, so adding a guide never again means adding a renderer
 * branch beside its enum entry.
 *
 * The legacy adapter is deliberately conservative. `overview` is the doorway
 * and `practical_strategies` is the only section that was ever a set of moves;
 * EVERYTHING else resolves to `insight`, and nothing ever resolves to `thread`.
 * In particular a frozen `conversation_starters` section must NOT become
 * `action` on the strength of carrying `items[]` — that would put "Build
 * practice" on a scripted speakable line, and the failure would surface
 * mid-conversation when the practice composer declines the code, not at click.
 * Its `leadStyle` is supplied here so those frozen rows keep rendering as the
 * serif quote-cards they always did.
 *
 * @param {Object} section - Guide content section
 * @returns {{contentType: string, leadStyle: string|null}} Resolved contract
 */
export function resolveSectionContract(section) {
  if (section?.contentType) {
    return { contentType: section.contentType, leadStyle: section.leadStyle || null };
  }

  switch (section?.type) {
    case 'overview':
      return { contentType: GuideContentTypes.DOORWAY, leadStyle: null };
    case 'practical_strategies':
      return { contentType: GuideContentTypes.ACTION, leadStyle: null };
    case 'conversation_starters':
      return { contentType: GuideContentTypes.INSIGHT, leadStyle: section.leadStyle || 'quote' };
    default:
      // An authored `leadStyle` is real data — honour it even on a row that
      // predates `contentType`, rather than dropping it on the legacy path.
      return { contentType: GuideContentTypes.INSIGHT, leadStyle: section?.leadStyle || null };
  }
}

/** A bold run leading its paragraph: `**Lead sentence.** body…` */
const LEAD_RE = /^\*\*(.+?)\*\*\s*([\s\S]*)$/;
/** The same, as a list item: `- **Lead.** body` / `1. **Lead.** body` */
const LIST_LEAD_RE = /^\s*(?:[-*+]|\d+[.)])\s+\*\*(.+?)\*\*\s*(.*)$/;

/**
 * A bold run is only a claim lead if it carries at least two words. Guards the
 * common false positives — an inline label (`**Note:** …`) or a sentence that
 * merely opens on a bold word (`**A** and **B** are both…`), where the
 * non-greedy match would otherwise take `A` as the lead and mangle the body.
 * @param {string} text
 * @returns {boolean}
 */
function isLeadLike(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean).length >= 2;
}

/**
 * Split a prose section's markdown into claim blocks.
 *
 * Replaces the previous bold-ONLY-line → `h4` promotion, which required a line
 * that was entirely bold (`/^\s*\*\*…\*\*\s*$/m`) and therefore never fired:
 * across both live guides the real corpus is 0 own-line bolds and 19 run-in
 * bolds (`**Lead.** body…`), so every prose section rendered as an unbroken
 * wall. The model is already writing claim leads; it just had nowhere typed to
 * put them.
 *
 * Handles the four shapes the model actually produces: a run-in bold paragraph,
 * a bulleted or numbered list of bold-led items (one paragraph, no blank lines
 * between items — a paragraph-anchored regex misses these entirely), a lead
 * containing nested emphasis (`**When you notice *that* pull.**` — a `[^*]+`
 * lead pattern breaks on the inner asterisks, hence non-greedy `.+?`), and
 * CRLF input (`\n{2,}` does not match `\r\n\r\n`).
 *
 * Once `blocks[]` is a generated field this stays as the degradation path for
 * frozen pre-`blocks` guides, which are immutable and can never be regenerated.
 *
 * @param {string} raw - Section markdown
 * @returns {{intro: string, blocks: Array<{lead: string, body: string}>}}
 */
export function parseProseBlocks(raw) {
  const text = String(raw || '').replace(/\r\n?/g, '\n').trim();
  if (!text) return { intro: '', blocks: [] };

  const blocks = [];
  let intro = '';

  for (const para of text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean)) {
    // A list paragraph: every non-empty line is a bold-led list item.
    const lines = para.split('\n').map(l => l.trim()).filter(Boolean);
    const listMatches = lines.map(l => l.match(LIST_LEAD_RE));
    if (listMatches.length > 0 && listMatches.every(m => m && isLeadLike(m[1]))) {
      listMatches.forEach(m => blocks.push({ lead: m[1].trim(), body: m[2].trim() }));
      continue;
    }

    const match = para.match(LEAD_RE);
    if (match && isLeadLike(match[1])) {
      blocks.push({ lead: match[1].trim(), body: match[2].trim() });
      continue;
    }

    // Unmatched: leading paragraphs are the section intro; later ones belong to
    // the block they follow.
    if (blocks.length === 0) intro = intro ? `${intro}\n\n${para}` : para;
    else blocks[blocks.length - 1].body += `\n\n${para}`;
  }

  return { intro, blocks };
}

/**
 * Total visible words across every rendered field. Counting `content` alone
 * undercounts badly once a section's substance lives in a typed array, so the
 * item/thread/block fields are counted too. `blocks` is counted only when it is
 * a real generated field — for a legacy row it is derived from `content` and
 * would double-count.
 * @param {Array<Object>} sections
 * @returns {number}
 */
function countWords(sections) {
  let total = 0;
  const add = (text) => {
    if (text) total += String(text).trim().split(/\s+/).filter(Boolean).length;
  };
  for (const section of sections) {
    add(section.content);
    if (Array.isArray(section.blocks)) section.blocks.forEach(b => { add(b.lead); add(b.body); });
    if (Array.isArray(section.items)) section.items.forEach(i => { add(i.kicker); add(i.title); add(i.body); });
    if (Array.isArray(section.threads)) section.threads.forEach(t => { add(t.headline); add(t.question); add(t.context); });
  }
  return total;
}

/**
 * The chat affordance each content type opens, keyed by the SAME resolved
 * content type the renderer uses. `doorway` is absent deliberately — the
 * overview has no atoms and no button.
 */
const ATOM_AFFORDANCE = {
  [GuideContentTypes.INSIGHT]: { label: 'Discuss this' },
  [GuideContentTypes.ACTION]: { label: 'Build experiment' },
  [GuideContentTypes.THREAD]: { label: 'Discuss thread' }
};

/**
 * The individual (You / solo) guide row currently rendered into the right
 * panel. The delegated click handlers resolve an atom's text from here rather
 * than from data attributes, so prose never round-trips through the DOM. Only
 * one guide panel is ever open, and every render replaces this wholesale.
 *
 * This is the INDIVIDUAL row only — never the shared artifact. The Together
 * half keeps its own slot ({@link renderedSharedGuide}); the two rendered rows
 * are never the same object, and the click handler picks between them by the
 * part wrapper the atom sits in.
 * @type {Object|null}
 */
let renderedGuide = null;

/**
 * The couple's shared-guide row currently rendered as the Together part, or
 * null when no readable Together part is on screen. Assigned by
 * `renderTogetherPart` and nowhere else. Kept separate from `renderedGuide`
 * because a Together atom's hand-off carries `{ sharedGuideDefinitionId,
 * sectionType, blockIndex }` — never a `UserGuide` id — so the two rows must
 * never be confused for one another.
 * @type {Object|null}
 */
let renderedSharedGuide = null;

/**
 * Render one atom's chat affordance: a quiet inline action — a label plus the
 * arrow-up-right, the app's shared "this opens somewhere else" mark — with no
 * outline and no ground, seated at the END of the body's final paragraph
 * (see `seatTrailingAction`) so it reads like a footnote link rather than a
 * control parked under every block. ONE form for every carrier — move cards,
 * thread cards and prose claim blocks alike — so a claim's hand-off looks like
 * every other hand-off in the document. Always visible: the hand-off must
 * survive touch, where hover-revealed affordances don't exist. The label is
 * wrapped in a `<span>` so the hover underline targets the text and not the
 * icon.
 * Returns '' for a content type with no affordance (`doorway`) and for derived
 * blocks (`contentType` null), which have no stable server-side index.
 * @param {string|null} contentType - Resolved content type
 * @param {string} sectionType - Section slug
 * @param {number} index - Atom index within its section's carrier
 * @returns {string} HTML string
 */
function renderAtomButton(contentType, sectionType, index) {
  const affordance = contentType ? ATOM_AFFORDANCE[contentType] : null;
  if (!affordance) return '';
  return `
    <button type="button" class="guide-atom__action" data-guide-atom="${escapeHtml(contentType)}"
      data-guide-atom-section="${escapeHtml(sectionType)}" data-guide-atom-index="${index}">
      <span>${escapeHtml(affordance.label)}</span>${iconArrowUpRight()}
    </button>
  `;
}

/**
 * Seat an atom's affordance inside the last paragraph of its body, after the
 * final sentence, so the hand-off reads as a trailing link rather than a
 * separate row. A body with no paragraph (a thread with no context note) gets
 * one paragraph holding just the affordance; an empty affordance (doorway,
 * derived block, read-only render) leaves the body untouched.
 * @param {string} bodyHtml - Rendered body markup (zero or more `<p>`s)
 * @param {string} buttonHtml - Output of `renderAtomButton`, possibly ''
 * @returns {string} Body markup with the affordance seated
 */
function seatTrailingAction(bodyHtml, buttonHtml) {
  if (!buttonHtml) return bodyHtml;
  const at = bodyHtml.lastIndexOf('</p>');
  if (at === -1) return `${bodyHtml}<p>${buttonHtml}</p>`;
  // The gap after the sentence is a real space (widened by `word-spacing`),
  // not a margin: a margin travels with the affordance when it wraps to the
  // next line and indents it, while trailing whitespace at a line end is
  // dropped, so the gap only exists when the affordance sits inline.
  return `${bodyHtml.slice(0, at)}<span class="guide-atom__gap"> </span>${buttonHtml}${bodyHtml.slice(at)}`;
}

/**
 * Render a section's typed `items[]` (the moves of an `action` section — and,
 * on frozen pre-`blocks` rows, the conversation prompts that predate the
 * `insight`/`blocks` shape) in the SAME block rhythm as a claim: kicker, lead,
 * body, one left edge. What differs is the gutter marker — a hollow ring for
 * a move (a thing to do, no order asserted), the hanging open-quote for a
 * speakable line — so the marker, not a box, says what kind of entry it is.
 * @param {Object} section - Guide content section with a non-empty `items`
 * @param {string|null} leadStyle - Resolved lead style (see resolveSectionContract)
 * @param {string} contentType - Resolved content type, for the per-atom button
 * @returns {string} HTML string
 */
function renderGuideItems(section, leadStyle, contentType) {
  const isPrompts = leadStyle === 'quote';
  return section.items.map((item, i) => `
    <div class="guide-block ${isPrompts ? 'guide-block--line' : 'guide-block--move'}">
      ${item.kicker ? `<span class="guide-block__kicker">${escapeHtml(item.kicker)}</span>` : ''}
      <p class="guide-block__lead${isPrompts ? ' guide-block__lead--quote' : ''}">${escapeHtml(item.title)}</p>
      <div class="guide-block__body">${seatTrailingAction(renderMarkdown(item.body), renderAtomButton(contentType, section.type, i))}</div>
    </div>
  `).join('');
}

/**
 * A thread's body is its optional context note plus the seated affordance;
 * with neither there is no body element at all.
 * @param {string|undefined} context - The thread's context note
 * @param {string} buttonHtml - Output of `renderAtomButton`, possibly ''
 * @returns {string} HTML string ('' when there is nothing to show)
 */
function renderThreadBody(context, buttonHtml) {
  const body = context ? `<p>${escapeHtml(context)}</p>` : '';
  const seated = seatTrailingAction(body, buttonHtml);
  return seated ? `<div class="guide-block__body">${seated}</div>` : '';
}

/**
 * Render a section's typed `threads[]` — the open questions of a `thread`
 * section — in the block rhythm: the headline is the kicker, the question the
 * lead, the context note the aside, and a question mark in the gutter says
 * what kind of entry it is.
 * @param {Object} section - Guide content section with a non-empty `threads`
 * @param {string} contentType - Resolved content type, for the per-atom button
 * @returns {string} HTML string
 */
function renderGuideThreads(section, contentType) {
  return section.threads.map((thread, i) => `
    <div class="guide-block guide-block--question">
      ${thread.headline ? `<span class="guide-block__kicker">${escapeHtml(thread.headline)}</span>` : ''}
      <p class="guide-block__lead">${escapeHtml(thread.question || '')}</p>
      ${renderThreadBody(thread.context, renderAtomButton(contentType, section.type, i))}
    </div>
  `).join('');
}

/**
 * Render a prose section's claim blocks. `leadStyle: 'quote'` marks the block
 * as a line to say: the lead is set upright between real quotation marks and
 * the gutter carries a hanging open-quote instead of the claim bullet.
 * @param {Array<{lead: string, body: string, kicker?: string}>} blocks
 * @param {string|null} leadStyle
 * @param {string} contentType - Resolved content type, for the per-atom button
 * @param {string} sectionType - Section slug, for the atom's server-side identity
 * @returns {string} HTML string
 */
function renderGuideBlocks(blocks, leadStyle, contentType, sectionType) {
  const isQuote = leadStyle === 'quote';
  return blocks.map((block, i) => `
    <div class="guide-block${isQuote ? ' guide-block--line' : ''}">
      ${block.kicker ? `<span class="guide-block__kicker">${escapeHtml(block.kicker)}</span>` : ''}
      <p class="guide-block__lead${isQuote ? ' guide-block__lead--quote' : ''}">${escapeHtml(block.lead)}</p>
      <div class="guide-block__body">${seatTrailingAction(renderMarkdown(block.body), renderAtomButton(contentType, sectionType, i))}</div>
    </div>
  `).join('');
}

/**
 * Render a single guide section: the doorway as a labelled grey block, every
 * other section as a numbered band + heading + its substance. Uses the
 * canonical shared `renderMarkdown` (marked.js + DOMPurify) — NOT a hand-rolled
 * regex renderer — so guide content gets the same sanitized formatting as every
 * other markdown surface in the app.
 *
 * Chrome is chosen by the RESOLVED content type, never by the section's `type`
 * slug (see resolveSectionContract). The substance carrier is chosen by which
 * array the row actually holds, so a guide generated against the newer contract
 * and a frozen pre-typed row both render correctly through the same path — that
 * is what let the generation contract ship ahead of this renderer. Legacy
 * `highlights` arrays on frozen pre-redesign guides are ignored.
 *
 * @param {Object} section - Guide content section
 * @param {number|null} num - 1-based display number for the sd band, or null
 *   for unnumbered sections (the doorway)
 * @param {Object} [opts]
 * @param {boolean} [opts.interactive=true] - false suppresses the per-atom chat
 *   affordance (see `renderGuideSections`) — the section wrapper's own
 *   `data-content-type` stays the REAL resolved type either way; only the
 *   carrier-renderer calls receive the suppressed value.
 * @param {string} [opts.idPrefix='guide-sec'] - Element-id prefix for the
 *   numbered sections. The merged reader renders two documents that both
 *   number their sections from 01, so each part needs its own id namespace
 *   (D11); every single-guide caller keeps the default.
 * @returns {string} HTML string
 */
function renderGuideSection(section, num, { interactive = true, idPrefix = 'guide-sec' } = {}) {
  const { contentType, leadStyle } = resolveSectionContract(section);

  // The doorway: a labelled block directly under the header, no numbered band
  // and no heading (the guide title is its heading). It never names its part:
  // inside a two-part document the enclosing `.guide-part` tints its ground
  // and ink, and the part band welded onto its top edge is its label — which
  // is why the eyebrow this emits is hidden by CSS under `.guide-part--railed`
  // rather than dropped here (one label per plate).
  if (contentType === GuideContentTypes.DOORWAY) {
    return `
      <div class="guide-section guide-section--overview" id="${escapeHtml(overviewSectionId(idPrefix))}" data-content-type="${escapeHtml(contentType)}" data-section-type="${escapeHtml(section.type)}">
        <div class="guide-overview">
          <p class="guide-overview__label">Overview</p>
          <div class="guide-section__content">${renderMarkdown(section.content || '')}</div>
        </div>
      </div>
    `;
  }

  // Number, heading and rule on ONE row. The number labels the heading rather
  // than floating above it in its own band, and the trailing rule keeps the
  // shared zone-divider grammar `.sd-section-header` established.
  const headingHtml = `
    <div class="guide-section__head">
      <span class="guide-section__num">${String(num).padStart(2, '0')}</span>
      <h3 class="guide-section__heading">${escapeHtml(section.heading)}</h3>
    </div>
  `;

  const intro = (content) => `<div class="guide-section__content">${renderMarkdown(content || '')}</div>`;

  // Suppression rides the mechanism the carrier renderers already use for
  // derived (unindexable) blocks below: `renderAtomButton` returns '' for a
  // falsy `contentType`. Passing `null` here (instead of
  // `contentType`) when `interactive` is false emits no atom markup and no
  // `data-guide-atom-*` attribute, without touching the wrapper's own
  // `data-content-type` (still `contentType` below, unconditionally).
  const atomType = interactive ? contentType : null;

  let bodyHtml;
  if (Array.isArray(section.threads) && section.threads.length > 0) {
    bodyHtml = intro(section.content) + renderGuideThreads(section, atomType);
  } else if (Array.isArray(section.items) && section.items.length > 0) {
    bodyHtml = intro(section.content) + renderGuideItems(section, leadStyle, atomType);
  } else if (Array.isArray(section.blocks) && section.blocks.length > 0) {
    bodyHtml = renderGuideBlocks(section.blocks, leadStyle, atomType, section.type);
  } else {
    // No typed carrier — split the section's markdown into claim blocks at
    // render time. The degradation path for frozen immutable rows. These blocks
    // are DERIVED, so they carry no per-atom button: the backend has no
    // `blocks[]` to index into and would have to re-run this parser to find the
    // claim (see AdviceStrategy._buildGuideInsightContext).
    const parsed = parseProseBlocks(section.content);
    bodyHtml = (parsed.intro ? intro(parsed.intro) : '')
      + (parsed.blocks.length > 0 ? renderGuideBlocks(parsed.blocks, leadStyle, null, section.type) : '');
  }

  return `
    <div class="guide-section" id="${escapeHtml(idPrefix)}-${num}" data-content-type="${escapeHtml(contentType)}" data-section-type="${escapeHtml(section.type)}">
      ${headingHtml}
      ${bodyHtml}
    </div>
  `;
}

/**
 * Launch the chat hand-off for one guide atom. Every branch resolves the atom
 * from the rendered row by (section, index) — the click target supplies
 * identity only.
 *
 * A Together atom (`together === true`) crosses as
 * `{ sharedGuideDefinitionId, sectionType, blockIndex }` under its own key
 * (`sharedGuideAction` / `sharedGuideInsight`): never the row's client-visible
 * typed `shared:` id, and never under `guideId`, so a `UserGuide` resolver can
 * never be handed a shared coordinate by accident. The backend resolves it
 * through `sharedGuideService.resolveForViewer`. Thread atoms are prose-based
 * and take the same `explore_question` path from either part.
 *
 * @async
 * @param {string} contentType - Resolved content type of the atom's section
 * @param {Object} guide - The rendered row (individual, or shared when `together`)
 * @param {Object} section - The atom's section
 * @param {number} index - Atom index within its carrier
 * @param {boolean} [together=false] - The atom sits in the Together part
 * @returns {Promise<void>}
 */
async function launchGuideAtom(contentType, guide, section, index, together = false) {
  if (contentType === GuideContentTypes.ACTION) {
    const item = (section.items || [])[index];
    if (!item?.title) return;
    if (together) {
      await launchPracticeBuild({
        sharedGuideAction: { sharedGuideDefinitionId: guide.guideDefinitionId, sectionType: section.type, blockIndex: index },
        opener: `Let's build an experiment around this from our guide: "${item.title}"`,
        source: 'shared_guide_action'
      });
      return;
    }
    // Identity only — same convention as the `guide_insight` branch below. The
    // backend reloads the user-owned READY guide server-side (entitlement-gated,
    // `TreatmentStrategy._resolveGuideActionHandoff`) and derives the handoff
    // fields itself; the client never builds or sends that prose. Any resolution
    // failure degrades loud to a cold build (a named admin event), not silently.
    await launchPracticeBuild({
      guideAction: { guideId: guide.id, sectionType: section.type, blockIndex: index },
      opener: `Let's build an experiment around this: "${item.title}"`,
      source: 'guide_action'
    });
    return;
  }

  if (contentType === GuideContentTypes.THREAD) {
    const thread = (section.threads || [])[index];
    if (!thread?.question) return;
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'ADVICE',
      guideThread: { question: thread.question, context: thread.context || '' }
    });
    return;
  }

  const block = (section.blocks || [])[index];
  if (!block?.lead) return;
  if (together) {
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'ADVICE',
      sharedGuideInsight: { sharedGuideDefinitionId: guide.guideDefinitionId, sectionType: section.type, blockIndex: index, lead: block.lead }
    });
    return;
  }
  eventBus.emit('navigate', {
    page: 'chat',
    mode: 'ADVICE',
    // Identity only — the backend reloads the user-owned READY guide and
    // extracts the claim itself. `lead` is display-only (the synthetic opener).
    guideInsight: { guideId: guide.id, sectionType: section.type, blockIndex: index, lead: block.lead }
  });
}

/**
 * One-time document-level delegated handler for the per-atom chat affordances.
 * Delegated for the same reason the contents menu is: all three consumer
 * sections insert the rendered body as an HTML string, and two of them re-swap
 * it once their guide fetch resolves, so anything bound to a node snapshot would
 * be observing a detached tree.
 */
let guideAtomsBound = false;
let atomLaunchInFlight = false;
function bindGuideAtoms() {
  if (guideAtomsBound) return;
  guideAtomsBound = true;

  document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-guide-atom]');
    if (!button) return;
    e.preventDefault();

    // Duplicate-click suppression. Each launch swaps the page and creates a
    // session; a double-tap would otherwise force TWO sessions, the second
    // closing the first mid-flight.
    if (atomLaunchInFlight) return;
    // The part wrapper tells the two halves apart: `GUIDE_PART_TOGETHER_ID` is
    // emitted only by `renderTogetherPart` (the solo reader never renders it,
    // and a Together-only card still wraps in it), so no `part` attribute needs
    // threading through the renderers.
    const together = !!button.closest('#' + GUIDE_PART_TOGETHER_ID);
    const guide = together ? renderedSharedGuide : renderedGuide;
    const section = (guide?.content?.sections || [])
      .find(s => s?.type === button.dataset.guideAtomSection);
    if (!guide || !section) {
      // A dead atom is loud, not a silent no-op: with two rows this is
      // reachable (a stale button after a re-render cleared its row).
      console.warn('[guideDrillHelper] atom row missing', { together, section: button.dataset.guideAtomSection });
      window.notificationManager?.showError('Could not open the conversation. Please try again.');
      return;
    }

    atomLaunchInFlight = true;
    button.disabled = true;
    try {
      await launchGuideAtom(button.dataset.guideAtom, guide, section, Number(button.dataset.guideAtomIndex), together);
    } catch (error) {
      console.error('[guideDrillHelper] Guide atom launch failed:', error);
      window.notificationManager?.showError('Could not open the conversation. Please try again.');
    } finally {
      atomLaunchInFlight = false;
      button.disabled = false;
    }
  });
}

/**
 * One-time document-level delegated jump handler for guide contents rows.
 *
 * Delegation (rather than per-render binding) because all three consumer
 * sections insert the rendered body as an HTML string, and two of them re-swap
 * that string a second time once their guide fetch resolves — so anything bound
 * to a snapshot of the nodes would be observing a detached tree. The contents
 * rows themselves now live in the panel HEADER's disclosure menu, which is
 * rebuilt on every open, so document-level delegation is also the only binding
 * that survives both lifecycles at once.
 */
let guideTocBound = false;
export function bindGuideToc() {
  if (guideTocBound) return;
  guideTocBound = true;

  document.addEventListener('click', (e) => {
    const jump = e.target.closest('[data-guide-jump]');
    if (!jump) return;
    e.preventDefault();
    const target = document.getElementById(jump.dataset.guideJump);
    if (!target) return;
    // A click on a contents-rail row moves the active row OPTIMISTICALLY, to
    // the clicked id — never by recomputing `activeSectionId`. The scroll
    // below is smooth and therefore async, so a recompute here reads the
    // PRE-scroll geometry and re-asserts the row the reader just left; the
    // spy's own callbacks confirm the new one as the scroll lands. Rows in the
    // panel-header Contents menu are outside any rail and are unaffected.
    const clickedRail = jump.closest?.('.guide-contents-rail');
    if (clickedRail) {
      applyGuideContentsActive(clickedRail, jump.dataset.guideJump);
      contentsSpyPending = { id: jump.dataset.guideJump, at: performance.now() };
    }
    // A zoned page's target wraps a disclosure (session detail): a jump to a
    // collapsed zone must open it, or the reader lands on a closed header.
    const toggle = target.querySelector(':scope > [data-disclosure-toggle]');
    if (toggle) {
      expandDisclosure(target, toggle.dataset.disclosureToggle, { scrollIntoView: true });
      return;
    }
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  });
}

/**
 * Resolve which numbered section the reader is currently in — the last one whose
 * jump-landing position (top minus its own `scroll-margin-top`) has passed just
 * under the scroller's top edge, so a Contents/rail jump always resolves to the
 * row it targeted; at the bottom of the scroller the last section always wins.
 *
 * Read on demand (when the contents menu opens) rather than continuously on
 * scroll: the menu is the only consumer of "where am I", so a scroll listener
 * would recompute this thousands of times to be read once.
 * @param {string} [selector='.guide-section[id]'] - Which elements count as
 *   sections; a non-guide consumer passes its own zone selector
 * @returns {string|null} Active section's element id, or null when none exist
 */
export function activeSectionId(selector = '.guide-section[id]') {
  const scroller = document.querySelector('.right-panel__content');
  const sections = [...(scroller?.querySelectorAll(selector) || [])];
  if (!scroller || sections.length === 0) return null;
  if (scroller.scrollHeight > 0 && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2) {
    return sections[sections.length - 1].id;
  }
  const line = scroller.getBoundingClientRect().top + 12;
  let active = sections[0];
  sections.forEach((el) => {
    const scrollMarginTop = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    if (el.getBoundingClientRect().top - scrollMarginTop <= line) active = el;
  });
  return active.id;
}

/**
 * Build the panel-header Contents menu for a guide, or null when it has fewer
 * than two numbered sections (a one-section guide needs no navigation).
 *
 * Returned as a `headerMenu` payload for RightPanelManager, which mounts it
 * through OptionsDropdown's disclosure shape so the chrome matches every other
 * header dropdown in the app. Rows carry `data-guide-jump`, so the delegated
 * handler above scrolls them with no per-open wiring.
 * @param {Object|null} guide - Fully-loaded guide row, or null while loading
 * @returns {Object|null} headerMenu config, or null when no menu is warranted
 */
export function buildGuideContentsMenu(guide) {
  if (!Array.isArray(guide?.content?.sections)) return null;
  return buildContentsMenu(contentsRows(guide));
}

/**
 * Build a panel-header Contents menu from a flat list of jump rows — the
 * generic form `buildGuideContentsMenu` and the session detail both use, so a
 * numbered document and a zoned page share one chrome
 * (`.guide-contents-menu__row`) and one delegated jump handler
 * (`bindGuideToc`, which this binds). Returns null below two rows: a page with
 * one zone needs no navigation.
 *
 * @param {Array<{id: string, label: string, num?: number}>} rows - Jump
 *   targets in page order; `num` renders as a leading `01`-style ordinal and
 *   is omitted for unnumbered zones
 * @param {Object} [opts]
 * @param {string} [opts.activeSelector='.guide-section[id]'] - Selector for
 *   the elements whose scroll position marks the current row
 * @returns {Object|null} headerMenu config, or null when no menu is warranted
 */
export function buildContentsMenu(rows, { activeSelector = '.guide-section[id]' } = {}) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  bindGuideToc();

  return {
    label: 'Contents',
    iconId: 'contents',
    menuClass: 'guide-contents-menu',
    body: () => {
      const activeId = activeSectionId(activeSelector);
      return rows.map(({ id, label, num }) => {
        const ordinal = Number.isInteger(num) ? `<b>${String(num).padStart(2, '0')}</b>` : '';
        return `<button type="button" class="guide-contents-menu__row" data-guide-jump="${escapeHtml(id)}" aria-current="${String(id === activeId)}">${ordinal}<span>${escapeHtml(label)}</span></button>`;
      }).join('');
    }
  };
}

/**
 * Render a list of guide sections standalone — no header, no contents menu,
 * no detail-panel wrapper. Runs the same doorway/overview-vs-numbered-body
 * split `renderGuideDetailBody` runs internally, so a second consumer (the
 * D4-lite shared-guide content body) gets identical section markup without
 * pulling in the whole own-guide panel.
 *
 * `interactive: false` renders a read-only copy: no per-atom "Discuss this" /
 * "Build experiment" chat affordance and zero `data-guide-atom-*` attributes
 * anywhere in the output. The Together part passes `false` only for a
 * preview row, whose atoms the server would refuse; a readable, entitled
 * Together part renders its atoms like the You part does. The section
 * wrapper's own `data-content-type` attribute, and the doorway branch, are
 * unaffected — see `renderGuideSection`.
 *
 * @param {Array<Object>} sections - Guide content sections
 * @param {Object} [opts]
 * @param {boolean} [opts.interactive=true] - false suppresses per-atom chat affordances
 * @param {string} [opts.idPrefix='guide-sec'] - Element-id prefix for the numbered
 *   sections; numbering restarts at 01 per call (D11)
 * @returns {string} HTML string (sections only — no wrapper element)
 */
export function renderGuideSections(sections, { interactive = true, idPrefix = 'guide-sec' } = {}) {
  let sectionNum = 0;
  const numbered = (sections || []).map(s => ({
    section: s,
    num: resolveSectionContract(s).contentType === GuideContentTypes.DOORWAY ? null : ++sectionNum
  }));
  return numbered
    .map(({ section, num }) => renderGuideSection(section, num, { interactive, idPrefix }))
    .join('');
}

/**
 * One-time document-level delegated handler for the preview gate's upgrade CTA.
 * Delegated for the same reason the atom and contents handlers are: the body is
 * inserted as an HTML string and re-swapped once the guide fetch resolves.
 *
 * The funnel surface is per-BUTTON, not per-module: the handler reads the key
 * `renderGuidePreviewGate` stamped into `data-guide-preview-upgrade` and falls
 * back to the guide-preview surface for a button carrying none, so a future
 * second gate surface needs no handler fork.
 */
let guidePreviewCtaBound = false;
export function bindGuidePreviewCta() {
  if (guidePreviewCtaBound) return;
  guidePreviewCtaBound = true;

  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-guide-preview-upgrade]');
    if (!button) return;
    e.preventDefault();
    goToUpgrade(eventBus, button.dataset.guidePreviewUpgrade || 'guidePreview');
  });
}

/**
 * Render the end-of-preview gate for a non-entitled reader: the headings of the
 * sections the server withheld, plus one upgrade CTA.
 *
 * FUNCTIONAL PLACEHOLDER. This is the minimum that makes the content gate
 * honest and navigable; the designed lock panel is explicitly deferred to the
 * launch-eve doc, which replaces this markup wholesale. Do not invest in it.
 *
 * Two known, accepted degradations of the preview render, both placeholder-era
 * cosmetics the same launch-eve pass resolves — do NOT try to fix them here:
 *   - `buildGuideContentsMenu` returns null below 2 non-doorway rows, so the
 *     panel-header Contents menu disappears on a preview.
 *   - The reading-time stamp counts the preview's words, not the full guide's.
 *
 * Headings are model output rendered into hand-rolled HTML, so every one is
 * escaped. `lockedSections` empty or absent renders NOTHING — a single-section
 * guide legitimately locks nothing, and an empty lock box reads worse than no
 * box at all. A locked section with no usable heading is simply omitted from
 * the list; the gate itself still renders, because something *is* withheld.
 *
 * The CTA copy stays neutral: trial eligibility is per-user (`trialAvailable`)
 * and is not plumbed to this renderer, so nothing here may promise a trial.
 *
 * The merged reader calls this ONCE with the two halves' `lockedSections`
 * concatenated (D6): both artifacts gate the same `{schemaVersion, sections}`
 * document through the same server-side seam, and one document must not paint
 * two identical paywalls. The CTA still carries its `UPGRADE_CLICK_SURFACES`
 * key as a data value so a future second surface needs no markup fork.
 *
 * @param {Array<{heading: string|null, type: string|null}>|undefined} lockedSections -
 *   Headings-only stubs from the server's preview projection
 * @param {Object} [opts]
 * @param {string} [opts.surface='guidePreview'] - Key into `UPGRADE_CLICK_SURFACES`
 * @returns {string} HTML string, or '' when nothing is withheld
 */
export function renderGuidePreviewGate(lockedSections, { surface = 'guidePreview' } = {}) {
  const locked = Array.isArray(lockedSections) ? lockedSections : [];
  if (locked.length === 0) return '';

  const rows = locked
    .map(s => (typeof s?.heading === 'string' ? s.heading.trim() : ''))
    .filter(Boolean)
    .map(heading => `<li class="guide-preview-gate__row">${escapeHtml(heading)}</li>`)
    .join('');

  return `
    <div class="guide-preview-gate">
      <h2 class="guide-preview-gate__title">The rest of this guide</h2>
      ${rows ? `<ul class="guide-preview-gate__list">${rows}</ul>` : ''}
      <p class="guide-preview-gate__note">You're reading a preview. The remaining sections are already written for you — Premium opens them.</p>
      <button type="button" class="btn btn--primary" data-guide-preview-upgrade="${escapeHtml(surface)}">See Premium</button>
    </div>
  `;
}

/**
 * Render the guide-detail panel body: a loading state while `guide` is not
 * yet loaded, an empty state when the loaded guide has no sections, or the
 * full title/subtitle/sections happy path.
 * @param {Object|null} guide - Raw guide (UserGuide row, `.content.sections`),
 *   or null/undefined while still loading
 * @returns {string} HTML string
 */
export function renderGuideDetailBody(guide) {
  if (!guide) {
    return `
      <div class="guide-detail">
        <div class="guide-detail__loading">
          <div class="loading-spinner"></div>
          <p>Loading guide...</p>
        </div>
      </div>
    `;
  }

  const content = guide.content;
  if (!content || !content.sections) {
    return `
      <div class="guide-detail">
        <div class="guide-detail__empty">
          <p>Guide content not available.</p>
        </div>
      </div>
    `;
  }

  // Generation date — a READY guide is immutable, so this is the single date it
  // was written. Absolute (not relative) so it never churns as time passes.
  const generatedLabel = guide.generatedAt
    ? new Date(guide.generatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  // Number the readable/doing sections; the doorway stays unnumbered.
  let sectionNum = 0;
  const numbered = content.sections.map(s => ({
    section: s,
    num: resolveSectionContract(s).contentType === GuideContentTypes.DOORWAY ? null : ++sectionNum
  }));
  const overview = numbered.filter(x => !x.num);
  const body = numbered.filter(x => x.num);

  const stamp = soloStampHtml(guide);

  // The panel-header Contents menu (buildGuideContentsMenu) is the only
  // navigation at tiers B/C; the rail below is the tier-A wide-panel
  // equivalent, hidden outside that container-query breakpoint (CSS, P4) —
  // both read the same rows (contentsRows), so the two can never drift.
  bindGuideToc();
  // The delegated atom handlers resolve their atom from this row, so it must be
  // set for the markup they are about to be handed.
  renderedGuide = guide;
  bindGuideAtoms();

  // `preview` is a TOP-LEVEL row flag (never inside `content`) — the server's
  // entitlement-aware serializer sets it, and `content.sections` is then the
  // doorway plus one body section rather than the whole guide.
  const previewGate = guide.preview === true ? renderGuidePreviewGate(guide.lockedSections) : '';
  if (previewGate) bindGuidePreviewCta();

  return `
    <div class="guide-detail">
      ${renderGuideContentsRail(contentsRows(guide))}
      <div class="guide-detail__main">
        <div class="guide-detail__header">
          <h1 class="sd-title">${escapeHtml(guide.title)}</h1>
          ${guide.subtitle ? `<p class="sd-subtitle">${escapeHtml(guide.subtitle)}</p>` : ''}
          ${stamp ? `<div class="sd-metadata">${stamp}</div>` : ''}
        </div>
        ${overview.map(({ section }) => renderGuideSection(section, null)).join('')}
        <div class="guide-detail__sections">
          ${body.map(({ section, num }) => renderGuideSection(section, num)).join('')}
        </div>
        ${previewGate}
      </div>
    </div>
  `;
}

/**
 * Build the `right-panel:open` payload for a fully-loaded guide. Header star
 * toggle + Options dropdown (Copy, Share, Archive/Unarchive, Delete), wired
 * from injected callbacks — the render stays pure and portable, mirroring
 * `buildNoteDetailPayload`. The whole dropdown comes from the shared
 * `buildGuideManagementDropdown`, so this surface can't drift from /library's
 * or /discovery's; only Archive/Delete route through the injected `onOption`.
 *
 * There is deliberately NO primary button. A guide is discussed one atom at a
 * time, from the atom itself (see `renderAtomButton`) — a whole-document
 * "Discuss guide" hand-off could only ever re-summarize what the reader is
 * already looking at.
 *
 * @param {Object} guide - Fully-loaded raw guide (UserGuide row) — must have
 *   `.guideDefinitionId` (the slug identity for star/archive/delete), `.id`
 *   (the row UUID, used by the per-atom hand-offs), `.title`, `.starredAt`,
 *   `.archivedAt`, `.content`
 * @param {Object} opts
 * @param {(nextStarred: boolean) => boolean|Promise<boolean>} opts.onStar -
 *   Header star toggle; resolves whether the toggle succeeded
 *   (RightPanelManager optimistically flips and reverts on a falsy result)
 * @param {(action: 'archive') => void} opts.onOption - Options-dropdown handler
 *   (Archive/Unarchive — no guide surface offers Delete)
 * @returns {Object} right-panel payload
 */
export function buildGuideDetailPayload(guide, { onStar, onOption }) {
  const content = document.createElement('div');
  content.innerHTML = renderGuideDetailBody(guide);

  return {
    title: '',
    titleAlign: 'left',
    content,
    contentType: GUIDE_DETAIL_CONTENT_TYPE,
    headerMenu: buildGuideContentsMenu(guide),
    // The tier-A rail wants room for its own column; a generic panel-shell
    // affordance (RightPanelManager nudges to this width, never shrinking).
    preferredWidth: 880,
    starButton: {
      starred: !!guide.starredAt,
      onClick: onStar
    },
    // `guide` is already fully loaded here (unlike InsightsSection's timing
    // exception), so the getter closure is trivial — it always has the same
    // guide to return.
    optionsDropdown: buildGuideManagementDropdown(() => guide, {
      archived: !!guide.archivedAt,
      onOption
    })
  };
}

/* ==========================================================================
   THE MERGED READER (hybrid guide)
   --------------------------------------------------------------------------
   One document in the hand, two artifacts underneath: the user's own
   `UserGuide` ("You") and the couple's `SharedGuide` ("Together"). This is
   PRESENTATION-LAYER COMPOSITION ONLY (D1) — two rows, two lifecycles, two
   gates, two payloads. Nothing here merges them, no shared section is ever
   rendered through the individual guide's data, and vice versa.

   The single-guide exports above keep their signatures and default id prefix
   (D18): `/discovery` and `/search` call them and are out of scope.
   ========================================================================== */

/** Element id of the merged reader's You part (rail jump target, D12 swap). */
export const GUIDE_PART_YOU_ID = 'guide-part-you';
/** Element id of the merged reader's Together part (rail jump target). */
export const GUIDE_PART_TOGETHER_ID = 'guide-part-together';
/** Element id of the receipt block (rail jump + Contents settings row). */
export const GUIDE_RECEIPT_ID = 'guide-receipt';
/** Element id of the "Together added" band (D5 sentinel + "Got it" host). */
export const GUIDE_TOGETHER_BAND_ID = 'guide-together-band';
/** Section-id prefix for the You part's sections (D11). */
const YOU_ID_PREFIX = 'guide-sec-you';
/** Section-id prefix for the Together part's sections (D11). */
const TOG_ID_PREFIX = 'guide-sec-tog';

/**
 * Element id of a document's doorway — the rail heading's jump target, and a
 * `.guide-section[id]` the contents spy already observes, which is what lets
 * the heading go current while the reader is in the doorway.
 * @param {string} idPrefix - The part's section-id prefix
 * @returns {string} Element id
 */
function overviewSectionId(idPrefix) {
  return `${idPrefix}-overview`;
}

/**
 * Whether the viewer's own reading is readable.
 *
 * Presence is read from STATUS, never from row presence (D19):
 * `getAvailableGuides` returns an availability row for every visible
 * definition whether or not a `UserGuide` exists, so "the object is here"
 * proves nothing. The same predicate holds for the availability row the
 * reader opens with and for the full row the D12 swap brings in — both carry
 * `status`.
 * @param {Object|null} individual - Availability row or fully-loaded UserGuide row
 * @returns {boolean}
 */
function isIndividualReadable(individual) {
  return individual?.status === 'READY';
}

/**
 * Whether the couple's reading is readable.
 *
 * `hasRenderableSections` is the gate (D19) so a malformed frozen row falls
 * back to the trailer's "being written" line rather than crashing a renderer.
 * The `withdrawn` conjunct is fail-closed hardening of the bilateral
 * visibility gate: a withdrawn row is not supposed to carry content at all,
 * and if one ever did, this reader must still not render it.
 *
 * Exported because the `/library` card's open gate reads it too: a card opens
 * when EITHER half is readable (D19), and a Together-only card must open.
 * @param {Object|null} shared - Shared-guide manifest item
 * @returns {boolean}
 */
export function isTogetherReadable(shared) {
  return !!shared
    && shared.viewerState !== 'withdrawn'
    && shared.status === 'ready'
    && hasRenderableSections(shared.content?.sections);
}

/**
 * Whether the couple's reading landed after the viewer last opened this guide
 * (D3). Fails CLOSED on a null `generatedAt`: a ready row with no generation
 * timestamp is NOT unread, and `sealedAt` is never used as a proxy (it fires
 * early — the artifact seals before it is written).
 *
 * Exported because three surfaces must agree on this one predicate: the band
 * inside the Together part, the dot on the panel-header Contents button, and
 * the badge key the Library card reads.
 * @param {Object|null} shared - Shared-guide manifest item
 * @returns {boolean}
 */
export function isTogetherUnread(shared) {
  return shared?.status === 'ready'
    && shared?.viewerState === 'sealed'
    && !!shared?.generatedAt
    && isNewSinceViewed(shared.generatedAt, shared.viewedAt);
}

/**
 * Whether a `UserGuide` row exists for this definition at all — the gate on
 * the header star (star acts on the `UserGuide` row, so a Together-only card
 * has none). An availability row for a definition the user has never
 * generated carries `status: null`.
 * @param {Object|null} individual
 * @returns {boolean}
 */
function hasUserGuideRow(individual) {
  return !!individual && (individual.hasGuide === true || !!individual.status);
}

/** The sections of whichever half is passed, or an empty array. */
function partSections(guide) {
  const sections = guide?.content?.sections;
  return Array.isArray(sections) ? sections : [];
}

/**
 * The merged reader's masthead stamp: the SAME two grey `.sd-metadata__text`
 * fact pills the solo reader carries (D-A) — one date, then the reading time
 * of the whole document — so a coupled masthead and a solo one read alike.
 *
 * The facts are document-level, summed across the halves the reader can
 * actually read: minutes are the total, and the date pill says "Written
 * {date}" when those halves agree on a date (or only one is readable) and
 * "Updated {date}", carrying the newer, when they differ. Which half is which,
 * and when each was written, is the contents map's job now — the masthead says
 * what the document is.
 *
 * Rebuilt (not patched in pieces) so the D12 swap has one thing to replace:
 * the first render knows the Together half's words but not the reader's own,
 * because that half is still in flight.
 *
 * Returns '' rather than an empty wrapper when nothing is known: the empty
 * stamp is hidden by `[data-guide-stamp]:empty`, which a whitespace node
 * would defeat.
 * @param {Object} parts
 * @param {Object|null} parts.individual
 * @param {Object|null} parts.shared
 * @returns {string} HTML string (inner HTML of the stamp row)
 */
function mergedStampHtml({ individual, shared }) {
  // No couple: the stamp a solo reader has always had.
  if (!shared) return soloStampHtml(individual);

  // A half counts toward the facts only once it is readable AND its words
  // have arrived: an availability row is readable with nothing to read yet.
  const halves = [];
  if (isIndividualReadable(individual) && hasRenderableSections(partSections(individual))) {
    halves.push(individual);
  }
  if (isTogetherReadable(shared)) halves.push(shared);

  const pills = [];
  // Timestamps, not formatted strings: a truthy-but-unparseable `generatedAt`
  // must drop out entirely rather than contribute an empty date, and
  // `Math.max(NaN, x)` is NaN, which would let one malformed half erase the
  // other's valid one.
  const stamps = halves
    .map(half => (half.generatedAt ? new Date(half.generatedAt).getTime() : NaN))
    .filter(Number.isFinite);
  if (stamps.length > 0) {
    const dates = new Set(stamps.map(ms => formatLongDate(new Date(ms).toISOString())));
    const newest = formatLongDate(new Date(stamps.reduce((a, b) => Math.max(a, b))).toISOString());
    if (newest) pills.push(`${dates.size > 1 ? 'Updated' : 'Written'} ${escapeHtml(newest)}`);
  }
  const minutes = minutesPill(halves.reduce((sum, half) => sum + countWords(partSections(half)), 0));
  if (minutes) pills.push(minutes);

  return factPillsHtml(pills);
}

/**
 * The single-document masthead stamp — every guide panel without parts (the
 * /discovery and /search panels, and a solo reader on /library): two grey fact
 * pills, "Written {date}" and "{N} min read", the meta-row grammar the other
 * detail pages use (style guide §4). Reading time counts every visible field,
 * not just `content` — a section whose substance lives in a typed array would
 * otherwise read as near-empty — and any non-empty guide is at least a minute.
 * @param {Object|null} guide - Guide row (any surface's shape)
 * @returns {string} HTML string
 */
function soloStampHtml(guide) {
  const pills = [];
  if (guide?.generatedAt) {
    pills.push(`Written ${escapeHtml(formatLongDate(guide.generatedAt))}`);
  }
  const sections = partSections(guide);
  if (hasRenderableSections(sections)) {
    const minutes = minutesPill(countWords(sections));
    if (minutes) pills.push(minutes);
  }
  return factPillsHtml(pills);
}

/** Whole minutes for a word count — any non-empty part is at least one. */
function readingMinutes(words) {
  return Math.max(1, Math.round(words / 220));
}

/**
 * The app's meta-row fact pills (style guide §4), the ONE emitter both guide
 * mastheads use — the merged reader's and the solo one's — so a coupled
 * masthead and an uncoupled one can never drift apart in markup. Empty in,
 * empty out: `[data-guide-stamp]:empty` hides the row, and a wrapper around
 * nothing (or a whitespace node) would defeat it.
 * @param {Array<string>} texts - Pill texts, already escaped
 * @returns {string} HTML string
 */
function factPillsHtml(texts) {
  return texts.map(text => `<span class="sd-metadata__text">${text}</span>`).join('');
}

/** The reading-time pill for a word count, or '' for a wordless document. */
function minutesPill(words) {
  return words > 0 ? `${readingMinutes(words)} min read` : '';
}

/** "Jul 24" — the card's date grammar, so the card and the contents map agree. */
function formatShortDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Whether the viewer's own half is still locked: an availability row with no
 * `UserGuide` row behind it and eligibility explicitly false. Locked means
 * discovery-path progress, not premium — that gate is `preview` /
 * `lockedSections` on the loaded row and is judged elsewhere.
 * @param {Object|null} individual - Availability row or UserGuide row
 * @returns {boolean}
 */
function isIndividualLocked(individual) {
  return Boolean(individual) && !individual.status && individual.isEligible === false;
}

/**
 * Re-render the masthead stamp in place. The D12 swap seam: the panel opens
 * before `API.getGuide` resolves, so the first stamp counts the Together half
 * alone; once the individual half lands the caller swaps the You part's node
 * and calls this with the loaded row. Rebuilt wholesale, never patched in
 * pieces — both facts can change at once.
 *
 * @param {ParentNode} root - The mounted panel content (or any ancestor of the stamp)
 * @param {Object} parts
 * @param {Object|null} parts.individual - Fully-loaded UserGuide row (or the availability row on failure)
 * @param {Object|null} parts.shared - Shared-guide manifest item
 * @returns {boolean} Whether a stamp element was found and patched
 */
export function patchMergedGuideStamp(root, { individual, shared } = {}) {
  const stamp = root?.querySelector?.('[data-guide-stamp]');
  if (!stamp) return false;
  stamp.innerHTML = mergedStampHtml({ individual, shared });
  return true;
}

/**
 * The masthead: title, subtitle, stamp. Title comes from the individual row
 * when there is one and from `shared.title` otherwise, so a Together-only
 * card (your own reading archived, the couple's not) still opens with a name.
 * @param {Object} parts
 * @returns {string} HTML string
 */
function renderMergedMasthead({ individual, shared }) {
  const title = individual?.title || shared?.title || 'Guide';
  const subtitle = individual?.subtitle || shared?.subtitle || '';
  return `
    <div class="guide-detail__header">
      <h1 class="sd-title">${escapeHtml(title)}</h1>
      ${subtitle ? `<p class="sd-subtitle">${escapeHtml(subtitle)}</p>` : ''}
      <div class="sd-metadata" data-guide-stamp>${mergedStampHtml({ individual, shared })}</div>
    </div>
  `;
}

/**
 * One part's sticky band: the part name in the part hue, the part's one-line
 * "who can see this" note, and one directional jump to the other part.
 *
 * Orientation, not a view switch — which is why it is a labelled band rather
 * than a segmented toggle. It is also the doorway plate's label: CSS welds it
 * onto the plate below so the two read as one slab, and the plate's own
 * eyebrow is hidden there. The weld is visual only — the band must stay a
 * SIBLING of the plate, because NO SCROLL-SPY (D11) rests on it being
 * `position: sticky` inside its own `.guide-part`: the pinned band is then
 * always the one belonging to the part you are reading and the browser does
 * the tracking. The jump carries `data-guide-jump` only, so the existing
 * document-delegated `bindGuideToc` handler scrolls it (reduced-motion
 * aware); there are no timers and no `scrollend`.
 *
 * @param {'you'|'together'} part - Which part this band belongs to
 * @param {Object|null} shared - Shared-guide manifest item
 * @param {Object} [opts]
 * @param {boolean} [opts.withYou=true] - Whether a You part exists to jump to
 *   (a Together-only card has none — see `renderMergedGuideBody`)
 * @returns {string} HTML string
 */
function renderGuidePartBand(part, shared, { withYou = true } = {}) {
  const isTogether = part === GUIDE_PART_TOGETHER;
  const who = isTogether
    ? 'You both see this part.'
    : 'Only you see this part.';
  let jump = '';
  if (isTogether && withYou) {
    jump = `<button type="button" class="guide-part-band__jump" data-guide-jump="${GUIDE_PART_YOU_ID}">You ↑</button>`;
  } else if (!isTogether && isTogetherReadable(shared)) {
    jump = `<button type="button" class="guide-part-band__jump" data-guide-jump="${GUIDE_PART_TOGETHER_ID}">Together ↓</button>`;
  }
  return `
    <div class="guide-part-band">
      <span class="guide-part-band__name">${isTogether ? 'Together' : 'You'}</span>
      <span class="guide-part-band__who">${who}</span>
      ${jump}
    </div>
  `;
}

/**
 * One You-part state plate, in the part doorway's own grammar.
 *
 * A state plate sits exactly where a written part's doorway would, so it IS
 * the doorway for a part with nothing to read: the same `.guide-overview`
 * wash and eyebrow (the enclosing `.guide-part` tints both), prose leaned
 * toward the part ink, and — when there is something to do — one action row
 * closing the plate with a single primary button at its left edge. The
 * eyebrow names the state and is the only thing that carries a colour, on the
 * /library card's rail semantics: red only when the writing failed, grey when
 * locked, the part's ink otherwise (`reflections.css`).
 *
 * @param {Object} opts
 * @param {string} opts.label - The eyebrow: the state, in a few words
 * @param {string} opts.body - The plate's one or two sentences
 * @param {string} [opts.state=''] - State name, emitted as `guide-state--{state}`
 * @param {string} [opts.detailHtml=''] - Trusted HTML between the prose and the
 *   action row (the locked plate's path card)
 * @param {string} [opts.action=''] - Button label; no action row when empty
 * @param {string} [opts.actionAttr=''] - The button's data attribute
 * @returns {string} HTML string
 */
function renderGuideStatePlate({ label, body, state = '', detailHtml = '', action = '', actionAttr = '' }) {
  const actions = action
    ? `<div class="guide-state__actions"><button type="button" class="btn btn--primary btn--small" ${actionAttr}>${escapeHtml(action)}</button></div>`
    : '';
  return `
    <div class="guide-overview guide-state${state ? ` guide-state--${state}` : ''}" role="note">
      <p class="guide-overview__label guide-state__label">${escapeHtml(label)}</p>
      <p class="guide-state__text">${escapeHtml(body)}</p>
      ${detailHtml}${actions}
    </div>
  `;
}

const COUNT_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];

/**
 * How many more sections a locked guide's path needs before the guide
 * unlocks, from the availability row's own counts — the whole path when it is
 * not complete, else the evidence floor. 0 when the row does not say.
 * @param {Object} individual - Availability row
 * @returns {number}
 */
function lockedSectionsRemaining(individual) {
  const done = Number(individual.completedSections);
  if (!Number.isFinite(done)) return 0;
  const target = individual.pathComplete === false
    ? Number(individual.totalSections)
    : (individual.minimumEvidenceMet === false ? Number(individual.minimumCompletedSections) : NaN);
  return Number.isFinite(target) ? Math.max(0, target - done) : 0;
}

/**
 * The locked plate's sentence: the path it is written from and how far off
 * the unlock is, composed from the row's counts so it stays true as the path
 * moves. Falls back to the server's `lockReason` (already carrying the real
 * path name) when the row does not carry a path name or the counts.
 * @param {Object} individual - Availability row
 * @returns {string} Plain text
 */
function lockedPlateBody(individual) {
  const pathName = individual.pathMetadata?.name;
  const remaining = lockedSectionsRemaining(individual);
  if (pathName && remaining > 0) {
    const count = remaining < COUNT_WORDS.length ? COUNT_WORDS[remaining] : String(remaining);
    return `This guide is written from the ${pathName} path. ${count} more ${remaining === 1 ? 'section' : 'sections'} and it unlocks.`;
  }
  return individual.lockReason || 'Finish its discovery path to unlock it.';
}

/**
 * The path inside a locked plate, as a fact rather than a sentence: the
 * path's disc wearing the same progress ring the /discovery rows draw
 * (`pathDisc.discOverlaysHtml`), its name, one bar segment per section with
 * the finished ones filled, and the count. Nothing here is a control — the
 * plate's action row below it is.
 * @param {Object} individual - Availability row
 * @returns {string} HTML string ('' when the row names no path)
 */
function renderLockedPathCard(individual) {
  const path = individual.pathMetadata;
  if (!path?.name) return '';
  const total = Number(individual.totalSections) || 0;
  const done = Math.min(total, Number(individual.completedSections) || 0);
  const image = path.cardContent?.image ? `assets/images/paths/${path.cardContent.image}` : '';
  // Same missing-asset treatment as the /discovery rows: the img hides itself
  // and the neutral disc stays, rather than a broken-image glyph.
  const art = image
    ? `<img class="guide-state-path__art" src="${escapeHtml(image)}" alt="" onerror="this.classList.add('is-missing')">`
    : '';
  const ring = discOverlaysHtml({ percent: total > 0 ? (done / total) * 100 : null });
  const segments = total > 0
    ? `<div class="guide-state-path__segments" aria-hidden="true">${Array.from({ length: total }, (_, i) => `<i${i < done ? ' class="is-done"' : ''}></i>`).join('')}</div>`
    : '';
  const meta = total > 0
    ? `<p class="guide-state-path__meta"><b>${done} of ${total}</b> sections</p>`
    : '';
  return `
      <div class="guide-state-path">
        <span class="path-disc guide-state-path__disc">${art}${ring}</span>
        <div class="guide-state-path__body">
          <p class="guide-state-path__name">${escapeHtml(path.name)}</p>
          ${segments}${meta}
        </div>
      </div>
  `;
}

/**
 * The You part's non-section body — every state in which there are no
 * sections to read. Ordered by what the caller knows (a failed fetch, content
 * in flight), then by the row's own lifecycle: writing, error, unlocked but
 * never written, still locked (the path it is written from, how far off the
 * unlock is, the path card, and a "Continue this path" CTA when the row names
 * the unlocking `pathId`), and a generic fallback.
 *
 * @param {Object|null} individual - Availability row or UserGuide row
 * @param {Object} opts
 * @param {boolean} opts.failed - The D12 fetch came back null / an error envelope
 * @param {boolean} opts.readable - `status === 'READY'` (so the content is merely in flight)
 * @returns {string} HTML string
 */
function renderYouStateBlock(individual, { failed, readable }) {
  if (failed) {
    return renderGuideStatePlate({
      label: "Couldn't be loaded",
      body: 'Nothing was lost. Try again to open your guide.',
      state: 'error',
      action: 'Try again',
      actionAttr: 'data-guide-retry'
    });
  }

  if (readable) {
    return `
      <div class="guide-detail__loading">
        <div class="loading-spinner"></div>
        <p>Loading guide...</p>
      </div>
    `;
  }

  if (individual?.status === 'GENERATING') {
    return renderGuideStatePlate({
      label: 'Being written',
      body: 'This usually takes a couple of minutes. You can leave and come back; it will be here.',
      state: 'writing'
    });
  }

  if (individual?.status === 'ERROR') {
    return renderGuideStatePlate({
      label: "Couldn't be written",
      body: "Nothing was lost. Your answers are all still here, and writing can start again whenever you're ready.",
      state: 'error',
      action: 'Try again',
      actionAttr: 'data-guide-retry'
    });
  }

  if (individual?.isEligible === true) {
    return renderGuideStatePlate({
      label: 'Not yet written',
      state: 'eligible',
      body: "Your guide is written from your own answers, and only you see it. It takes a couple of minutes, and you can leave while it's being written.",
      action: 'Write my guide',
      actionAttr: 'data-guide-generate'
    });
  }

  if (isIndividualLocked(individual)) {
    // Locked means discovery-path progress — never premium. The plate says
    // which path and how far off the unlock is, shows the path itself, and
    // the CTA needs a path to go to, so it is omitted on a row without one.
    return renderGuideStatePlate({
      label: 'Still locked',
      state: 'locked',
      body: lockedPlateBody(individual),
      detailHtml: renderLockedPathCard(individual),
      action: individual.pathId ? 'Continue this path' : '',
      actionAttr: 'data-guide-continue-path'
    });
  }

  return renderGuideStatePlate({ label: 'Not yet written', body: "Your guide isn't written yet." });
}

/**
 * The Together trailer (D17): what follows the You part in every state where
 * the Together part is NOT rendered (`pending-review`, `waiting`, `sealed`
 * but not ready, `withdrawn`).
 *
 * It is the Together colophon plate carrying the state's closing line in
 * place of the lede, the receipt where one exists, and the show/hide switch
 * — the ONLY revoke/re-enable UI shared guides have, so it must never
 * disappear: a user who withdrew has to be able to come back from here.
 *
 * A3 silence: the copy states facts, never causes. The floored-out `waiting`
 * case (both partners confirmed, nothing sealed) keeps the generic line via
 * `isFlooredOutWaiting` and never names the partner; `withdrawn` says the
 * reading is hidden, never who hid it.
 *
 * @param {Object|null} shared - Shared-guide manifest item
 * @param {Object} [opts]
 * @param {string} [opts.partnerName] - Resolved partner display name
 * @returns {string} HTML string ('' when the Together part renders instead)
 */
function renderTogetherTrailer(shared, { partnerName } = {}) {
  if (!shared || isTogetherReadable(shared)) return '';

  const name = escapeHtml(partnerName || 'your partner');
  let line;
  let changeLabel;

  if (shared.viewerState === 'pending-review') {
    line = 'A <b>Together</b> guide is written once you both share.';
    // The plate's one Partner-page link is worded for the state: nothing has
    // been shared yet, so it invites a review rather than a change.
    changeLabel = "Review what you'd share ›";
  } else if (isFlooredOutWaiting(shared)) {
    line = "We're still preparing this guide — it becomes available as you both share more.";
  } else if (shared.viewerState === 'waiting') {
    line = `A <b>Together</b> guide is written once you both share. Waiting for ${name} to decide what they'll share.`;
  } else if (shared.viewerState === 'withdrawn') {
    line = 'The <b>Together</b> guide is hidden while sharing is off.';
  } else {
    line = 'The <b>Together</b> guide is being written.';
  }

  return `
    <div class="guide-part__trailer">
      ${renderTogetherColophon(shared, { id: GUIDE_RECEIPT_ID, stateHtml: line, changeLabel })}
    </div>
  `;
}

/**
 * The ONE preview gate for a non-entitled reader (D6): the two halves'
 * `lockedSections` are concatenated and `renderGuidePreviewGate` is called
 * once, under the single `guidePreview` funnel surface. Each artifact's preview
 * projection is still computed server-side by its own route — this only
 * refuses to paint two identical paywalls in one document. '' when neither
 * half withheld anything.
 *
 * The gate sits at the end of the LAST readable part's sections, ahead of that
 * part's closing material (the Together appendix or the You trailer), so the
 * document still ends on the receipt and the switch.
 * @param {Object} parts
 * @param {Object|null} parts.individual
 * @param {Object|null} parts.shared
 * @returns {string} HTML string
 */
function renderMergedPreviewGate({ individual, shared }) {
  const locked = [
    ...(individual?.preview === true ? (individual.lockedSections || []) : []),
    ...(shared?.preview === true && isTogetherReadable(shared) ? (shared.lockedSections || []) : [])
  ];
  const gate = renderGuidePreviewGate(locked);
  if (gate) bindGuidePreviewCta();
  return `<div class="guide-part__gate" data-guide-gate>${gate}</div>`;
}

/**
 * Re-render the preview gate in place — the second D12 swap seam. The
 * individual half's `preview`/`lockedSections` are only known once
 * `API.getGuide` resolves, and when the Together part is readable the gate
 * lives at the end of THAT part, which the swap does not replace; so the host
 * calls this beside {@link patchMergedGuideStamp} with the loaded row.
 * @param {ParentNode} root - The mounted panel content (or any ancestor of the gate slot)
 * @param {Object} parts
 * @param {Object|null} parts.individual - Fully-loaded UserGuide row (or the availability row on failure)
 * @param {Object|null} parts.shared - Shared-guide manifest item
 * @returns {boolean} Whether a gate slot was found and patched
 */
export function patchMergedGuideGate(root, { individual, shared } = {}) {
  const slot = root?.querySelector?.('[data-guide-gate]');
  if (!slot) return false;
  slot.outerHTML = renderMergedPreviewGate({ individual, shared });
  return true;
}

/**
 * Render the You part alone — the D12 swap seam.
 *
 * The panel opens before `API.getGuide` resolves, so the first render puts a
 * loading block here; when the fetch settles the caller replaces this node's
 * outerHTML with a second call carrying the loaded row (or `failed: true`),
 * then calls {@link patchMergedGuideStamp} and, when a trailer is present,
 * re-runs {@link wireMergedGuideBody} on the fresh node.
 *
 * `renderedGuide` (the individual row) is assigned HERE and nowhere else in
 * the merged reader, and is nulled on every non-section branch — so the
 * delegated atom handlers can never resolve a You atom against a stale row.
 * The shared artifact has its own slot, `renderedSharedGuide`, owned by
 * `renderTogetherPart`; the two are never the same object.
 *
 * @param {Object} parts
 * @param {Object|null} parts.individual - Availability row, or the loaded UserGuide row
 * @param {Object|null} parts.shared - Shared-guide manifest item (drives rail + trailer)
 * @param {string} [parts.partnerName] - Resolved partner display name
 * @param {boolean} [parts.failed=false] - The individual fetch came back unusable
 * @returns {string} HTML string — the whole `#guide-part-you` element
 */
export function renderGuideYouPart({ individual, shared, partnerName, failed = false } = {}) {
  const readable = isIndividualReadable(individual);
  const sections = partSections(individual);

  let body;
  if (!failed && readable && hasRenderableSections(sections)) {
    // The delegated atom handlers resolve a You atom from this row, so it must
    // be set for the markup they are about to be handed — the INDIVIDUAL half
    // only; the Together half sets `renderedSharedGuide`.
    renderedGuide = individual;
    bindGuideAtoms();
    body = renderGuideSections(sections, { interactive: true, idPrefix: YOU_ID_PREFIX });
  } else {
    renderedGuide = null;
    body = renderYouStateBlock(individual, { failed, readable });
  }

  // The band exists only when a couple does (D4): a solo reader's document has
  // one part and nothing to jump between.
  const rail = shared ? renderGuidePartBand(GUIDE_PART_YOU, shared) : '';
  // The gate closes the last readable part: it is the You part's only when the
  // Together part is not rendered (else it sits at the end of that part).
  const gate = isTogetherReadable(shared) ? '' : renderMergedPreviewGate({ individual, shared });

  return `<div class="guide-part guide-part--you${rail ? ' guide-part--railed' : ''}" id="${GUIDE_PART_YOU_ID}">${rail}${body}${gate}${renderTogetherTrailer(shared, { partnerName })}</div>`;
}

/**
 * The "Together added" band. Rendering only — the "Got it" click and the D5
 * IntersectionObserver that watches this element are wired by the host
 * section, which owns the view-endpoint write. The band states the fact
 * ("Added {date}"), never the cause (A3).
 * @param {Object} shared - Shared-guide manifest item (ready + unread)
 * @returns {string} HTML string
 */
function renderTogetherBand(shared) {
  return `
    <div class="guide-band" id="${GUIDE_TOGETHER_BAND_ID}">
      <span class="guide-band__dot" aria-hidden="true"></span>
      <p class="guide-band__text">Added ${escapeHtml(formatLongDate(shared.generatedAt))} — after you last read this guide.</p>
      <button type="button" class="guide-band__ack" data-guide-together-ack>Got it</button>
    </div>
  `;
}

/**
 * Render the Together part — '' unless the couple's reading is readable.
 *
 * Atoms render only when the part is readable AND the row is not a preview
 * (`shared.preview !== true`): a preview row's body section would otherwise
 * carry a button the server refuses. `renderedSharedGuide` is assigned HERE
 * and nowhere else (null when the part is not rendered), and the atom handler
 * is bound here too so a Together-only card (`withYou === false`) is bound.
 * `renderedGuide` stays the individual row.
 *
 * @param {Object} parts
 * @param {Object|null} parts.shared
 * @param {boolean} [parts.withYou=true] - Whether a You part precedes it
 * @param {Object|null} [parts.individual=null] - The individual half, read ONLY
 *   for its `lockedSections` so the one preview gate can close the document
 * @returns {string} HTML string
 */
function renderTogetherPart({ shared, withYou = true, individual = null }) {
  if (!isTogetherReadable(shared)) {
    renderedSharedGuide = null;
    return '';
  }

  const togetherInteractive = shared.preview !== true;
  renderedSharedGuide = shared;
  bindGuideAtoms();

  return `
    <div class="guide-part guide-part--together guide-part--railed" id="${GUIDE_PART_TOGETHER_ID}">
      ${renderGuidePartBand(GUIDE_PART_TOGETHER, shared, { withYou })}
      ${isTogetherUnread(shared) ? renderTogetherBand(shared) : ''}
      ${renderGuideSections(shared.content.sections, { interactive: togetherInteractive, idPrefix: TOG_ID_PREFIX })}
      ${renderMergedPreviewGate({ individual, shared })}
      <div class="guide-part__appendix">
        ${renderTogetherColophon(shared, { id: GUIDE_RECEIPT_ID })}
      </div>
    </div>
  `;
}

/**
 * Render the merged reader body: masthead, You part, Together part. The ONE
 * preview gate (D6) is rendered by whichever part is the last readable one —
 * see `renderMergedPreviewGate`.
 *
 * @param {Object} parts
 * @param {Object|null} parts.individual - Availability row, or the loaded UserGuide row
 * @param {Object|null} parts.shared - Shared-guide manifest item, or null when there is no couple
 * @param {string} [parts.partnerName] - Resolved partner display name
 * @param {'you'|'together'|null} [parts.pendingPart] - The part a deprecated
 *   `?sharedGuide=` deep link asked for. Stamped onto the root as
 *   `data-guide-pending-part` for the host to consume and scroll to after
 *   mount; there is no `part` URL param (D10).
 * @returns {string} HTML string
 */
export function renderMergedGuideBody({ individual, shared, partnerName, pendingPart = null } = {}) {
  // Contents-rail rows, contents-menu rows and band jumps share the one
  // document-delegated handler.
  bindGuideToc();

  // A Together-only card (no individual half at all — yours is archived, or was
  // never generated) renders the couple's part alone: an empty You part with a
  // state block would be inventing a half the card does not have. The trailer
  // still renders, standalone, because the switch inside it is the only
  // re-enable path shared guides have and may never disappear (D17).
  const withYou = !!individual;
  if (!withYou) renderedGuide = null;
  if (!shared) renderedSharedGuide = null;

  return `
    <div class="guide-detail guide-detail--merged"${pendingPart ? ` data-guide-pending-part="${escapeHtml(pendingPart)}"` : ''}>
      ${renderGuideContentsRail(contentsGroups({ individual, shared }))}
      <div class="guide-detail__main">
        ${renderMergedMasthead({ individual, shared })}
        ${withYou
    ? renderGuideYouPart({ individual, shared, partnerName })
    : renderTogetherTrailer(shared, { partnerName })}
        ${renderTogetherPart({ shared, withYou, individual })}
      </div>
    </div>
  `;
}

/**
 * Wire the merged body's one interactive control that is not delegated: the
 * shared-guide show/hide switch (in the Together part's appendix, or in the
 * trailer when that part is not rendered).
 *
 * Exposed separately from {@link buildMergedGuideDetailPayload} because the
 * D12 swap replaces the You part's node — trailer and all — so the host has to
 * re-wire the fresh node.
 *
 * @param {ParentNode} root - Panel content node, or the freshly swapped part
 * @param {Object} [opts]
 * @param {(nextEnabled: boolean) => Promise<boolean>} [opts.onToggleEnabled] - Host callback
 */
export function wireMergedGuideBody(root, { onToggleEnabled } = {}) {
  wireEnableToggle(root, onToggleEnabled);
}

/**
 * One part's Contents rows: the numbered (non-doorway) sections, numbered from
 * 01 within their own part and targeting that part's id namespace (D11). The
 * ONE row-group primitive both `buildGuideContentsMenu`/`buildMergedGuideContentsMenu`
 * and `renderGuideContentsRail` build on (via `contentsRows`/`contentsGroups`
 * below), so a row can never exist in one surface and not the other.
 * @param {Array<Object>} sections - That part's content sections
 * @param {string} idPrefix - The part's section-id prefix
 * @returns {Array<{id: string, num: number, label: string}>}
 */
function contentsRowsFor(sections, idPrefix) {
  let n = 0;
  return sections
    .filter(sec => resolveSectionContract(sec).contentType !== GuideContentTypes.DOORWAY)
    .map((sec) => {
      n += 1;
      return { id: `${idPrefix}-${n}`, num: n, label: sec.heading };
    });
}

/**
 * The solo reader's (`/discovery`, `/search`) Contents rows — a thin wrapper
 * over `contentsRowsFor` using the default `guide-sec` id namespace, so its
 * output targets the ids `renderGuideDetailBody` emits.
 * @param {Object|null} guide - Fully-loaded guide row (or availability row)
 * @returns {Array<{id: string, num: number, label: string}>} Empty when the row
 *   carries no sections array
 */
export function contentsRows(guide) {
  const sections = guide?.content?.sections;
  return Array.isArray(sections) ? contentsRowsFor(sections, 'guide-sec') : [];
}

/**
 * The merged reader's row-group model (§3.4): one thin wrapper over
 * `contentsRowsFor` shared by `buildMergedGuideContentsMenu` and
 * `renderGuideContentsRail`, so the header menu and the rail can never drift.
 *
 * `you` is `null` exactly when the individual half is not readable
 * (`isIndividualReadable`) — no You group at all, in either surface. Once
 * readable it is always an array, even before `content.sections` has loaded
 * (the availability row it opens with): zero rows until the D12 swap lands,
 * which is what lets `patchGuideContentsRail` fill the SAME group in place
 * rather than materializing a new one.
 *
 * `together` is `null` only when there is no couple at all (`shared` falsy —
 * the uncoupled reader); otherwise it is always `{rows, readable}`, `rows`
 * empty when `readable` is false. `readable` is `isTogetherReadable`'s
 * verdict — the one source of truth a renderer reads to decide the D8
 * wordless ghost heading, never chip rendering.
 *
 * `together.unread` is `isTogetherUnread`'s verdict — the dot the rail's
 * Together heading wears until the couple's half is acknowledged.
 *
 * `receipt` is true whenever a couple exists at all: the colophon plate that
 * carries the enable/disable switch renders in every state a couple exists,
 * so the settings row (menu) / foot (rail) that jumps to it does too.
 *
 * `meta` is each half's WRITTEN DATE, formatted short ("Sep 6", '' when
 * absent or unparseable) — the per-part fact the masthead used to carry on a
 * chip, and nothing else: no minutes, because the 13.5rem rail track is the
 * tightest space in the reader and the summed figure is already in the
 * masthead. It is a PARALLEL map rather than a field on each group because
 * `you`'s array-ness is contractual: the renderers gate the You group on
 * `Array.isArray(you)` and that gate fails SILENTLY, so an object with a
 * `meta` field would yield no You group and no error. Renderers read
 * `meta?.you`.
 *
 * Populated only when `together` is non-null (D-K): per-part dates exist to
 * tell parts apart, and a one-part document has nothing to tell apart — its
 * date is the masthead's. That also keeps the rail and the header menu in
 * step without a `coupled` flag, since neither shows meta for a one-part
 * reader.
 *
 * @param {Object} parts
 * @param {Object|null} parts.individual - Availability row, or the loaded UserGuide row
 * @param {Object|null} parts.shared - Shared-guide manifest item, or null when there is no couple
 * @returns {{you: Array<{id: string, num: number, label: string}>|null, together: {rows: Array<{id: string, num: number, label: string}>, readable: boolean, unread: boolean}|null, receipt: boolean, meta: {you: string, together: string}}}
 */
export function contentsGroups({ individual, shared } = {}) {
  const you = isIndividualReadable(individual)
    ? contentsRowsFor(partSections(individual), YOU_ID_PREFIX)
    : null;
  const togReadable = isTogetherReadable(shared);
  const together = shared
    ? {
      rows: togReadable ? contentsRowsFor(partSections(shared), TOG_ID_PREFIX) : [],
      readable: togReadable,
      unread: isTogetherUnread(shared)
    }
    : null;
  const meta = together
    ? {
      you: individual?.generatedAt ? formatShortDate(individual.generatedAt) : '',
      together: shared?.generatedAt ? formatShortDate(shared.generatedAt) : ''
    }
    : { you: '', together: '' };
  return { you, together, receipt: Boolean(shared), meta };
}

/**
 * Minimal 12px stroke settings-gear icon for the contents rail's
 * sharing/visibility foot row. No larger icon helper for this row exists
 * elsewhere in the codebase (the header Contents menu's settings row uses a
 * plain "·" ordinal instead), so this is a small local one rather than a
 * shared export — the rail is presently its only consumer.
 * @returns {string} Inline SVG markup
 */
function iconGuideContentsSettings() {
  return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="1.6" stroke="currentColor" stroke-width="1.1"/>
      <path d="M6 1.3v1.4M6 9.3v1.4M10.7 6H9.3M2.7 6H1.3M9.1 2.9l-1 1M3.9 8.1l-1 1M9.1 9.1l-1-1M3.9 3.9l-1-1" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>
    </svg>`;
}

/**
 * Render one numbered contents row — shared by the rail's You/Together
 * groups and its flat solo-reader list, so a row looks identical everywhere
 * it appears.
 * @param {{id: string, num: number, label: string}} row
 * @returns {string} HTML string
 */
function renderGuideContentsRailRow({ id, num, label }) {
  return `<button type="button" class="guide-contents-rail__row" data-guide-jump="${escapeHtml(id)}" aria-current="false"><b>${String(num).padStart(2, '0')}</b><span>${escapeHtml(label)}</span></button>`;
}

/**
 * Render the tier-A "In this guide" contents rail (§3.3), from the SAME row
 * model {@link contentsGroups}/{@link contentsRows} feed the header Contents
 * menu, so a row can never exist in one surface and not the other.
 *
 * Two call shapes:
 *  - **Merged** (`/library`'s two-part reader): pass the `contentsGroups`
 *    object (`{you, together, receipt}`). When `together === null` AND
 *    `receipt === false` — no couple exists at all, the uncoupled reader —
 *    this renders the same flat shape as the solo case below (title + rows
 *    from `you`, no group headings, no foot, D8); this is the ONLY groups
 *    shape that takes the flat path. Otherwise: `you === null` renders no
 *    You group at all (the individual half is not readable); an array (even
 *    empty, while the D12 swap is still in flight) renders the "You" heading
 *    with whatever rows it holds. A readable part's heading is itself a row,
 *    jumping to that part's doorway. `together.readable === false` renders
 *    the "Together" heading ALONE — a plain label, not a row, since there is
 *    nothing to jump to — in a ghost treatment, with no rows and no
 *    note of any kind (D8 — the rail is wordless about cause; the state
 *    plate and trailer elsewhere in the document are where that is said, and
 *    the rail never names the partner). `receipt` renders the foot: one row
 *    jumping to `GUIDE_RECEIPT_ID`. A readable heading also carries its
 *    half's written date from the parallel `meta` map, and the Together
 *    heading wears an unread dot while `together.unread` — the per-part facts
 *    the masthead used to carry on a chip.
 *  - **Solo** (`/discovery`, `/search`, and a solo `/library` reader): pass a
 *    flat rows array (from `contentsRows`), or `{rows}`. Renders the title
 *    plus a flat row list — no group headings, no foot.
 *
 * Never stamps `aria-current="true"` — every row renders `"false"`; the
 * active row is the scroll-spy's job (P3), not this renderer's.
 *
 * @param {{you: Array<{id,num,label}>|null, together: {rows: Array, readable: boolean, unread: boolean}|null, receipt: boolean, meta: {you: string, together: string}}|Array<{id,num,label}>|{rows: Array<{id,num,label}>}} groupsOrRows
 * @returns {string} HTML string — the whole `<nav class="guide-contents-rail">`
 */
export function renderGuideContentsRail(groupsOrRows) {
  const isUncoupledGroups = Boolean(
    groupsOrRows
    && !Array.isArray(groupsOrRows)
    && !Array.isArray(groupsOrRows.rows)
    && groupsOrRows.together === null
    && !groupsOrRows.receipt
  );

  const flatRows = Array.isArray(groupsOrRows)
    ? groupsOrRows
    : (Array.isArray(groupsOrRows?.rows)
      ? groupsOrRows.rows
      : (isUncoupledGroups ? (groupsOrRows.you || []) : null));

  const title = '<p class="guide-contents-rail__title">In this guide</p>';

  if (flatRows) {
    return `
      <nav class="guide-contents-rail app-scrollbar" aria-label="In this guide">
        ${title}
        ${flatRows.map(renderGuideContentsRailRow).join('')}
      </nav>
    `;
  }

  const { you = null, together = null, receipt = false, meta = null } = groupsOrRows || {};

  // A readable part's heading IS a row — the part's own doorway is a
  // destination, and the heading is the only thing in the rail that names it
  // (there is no "Overview" row: it would say the same word twice). It carries
  // the row class so the ONE spy/click path paints it like any other row, and
  // the heading class on top of it for the eyebrow type and the swatch.
  // A ghost heading (D8: the couple's part is not readable) stays a `<p>`:
  // nothing to jump to, so nothing to press — and carries neither date nor
  // dot, because a half that cannot be read has no date to offer and nothing
  // unread about it.
  //
  // The label rides in its OWN span so the row's ellipsis rule
  // (`.guide-contents-rail__row span`) reaches it; `__meta` is excluded from
  // that rule and pushed right with `margin-left: auto`. The dot goes BEFORE
  // the date, directly after the name, so the two parts' dates stay flush
  // right and aligned with each other whether or not a dot is present.
  const group = (key, label, rows, ghost, overviewId, date, unread) => {
    const inner = `<span class="guide-contents-rail__swatch"></span><span>${label}</span>`
      + (!ghost && unread ? '<span class="guide-contents-rail__dot" aria-hidden="true"></span>' : '')
      + (ghost || !date ? '' : `<span class="guide-contents-rail__meta">${escapeHtml(date)}</span>`);
    return `
    <div class="guide-contents-rail__group guide-contents-rail__group--${key}">
      ${ghost
    ? `<p class="guide-contents-rail__heading guide-contents-rail__heading--ghost">${inner}</p>`
    : `<button type="button" class="guide-contents-rail__row guide-contents-rail__heading" data-guide-jump="${escapeHtml(overviewId)}" aria-current="false">${inner}</button>`}
      ${ghost ? '' : rows.map(renderGuideContentsRailRow).join('')}
    </div>
  `;
  };

  const youHtml = Array.isArray(you)
    ? group('you', 'You', you, false, overviewSectionId(YOU_ID_PREFIX), meta?.you, false)
    : '';
  const togetherHtml = together
    ? group('together', 'Together', together.rows, !together.readable, overviewSectionId(TOG_ID_PREFIX), meta?.together, together.unread)
    : '';
  const foot = receipt
    ? `<div class="guide-contents-rail__foot"><button type="button" class="guide-contents-rail__row" data-guide-jump="${GUIDE_RECEIPT_ID}">${iconGuideContentsSettings()}<span>Sharing &amp; visibility</span></button></div>`
    : '';

  return `
    <nav class="guide-contents-rail app-scrollbar" aria-label="In this guide">
      ${title}
      ${youHtml}
      ${togetherHtml}
      ${foot}
    </nav>
  `;
}

/**
 * Re-render the contents rail in place — the D12 swap seam (§3.6), built
 * exactly like {@link patchMergedGuideStamp}: the You-part swap re-fetches
 * `contentsGroups` with the now-loaded individual row and replaces the rail
 * wholesale, so the "You" group's zero rows (rendered against the
 * availability row) become the real ones without a full panel re-render.
 * No-op when the mounted content carries no rail (a loading/empty
 * `renderGuideDetailBody` branch never emits one).
 *
 * @param {ParentNode} root - The mounted panel content (or any ancestor of the rail)
 * @param {Object|Array} groups - Same shape {@link renderGuideContentsRail} accepts
 * @returns {boolean} Whether a rail element was found and patched
 */
export function patchGuideContentsRail(root, groups) {
  const rail = root?.querySelector?.('.guide-contents-rail');
  if (!rail) return false;
  rail.outerHTML = renderGuideContentsRail(groups);
  return true;
}

/**
 * What the contents spy watches and measures: the same `.guide-section[id]`
 * elements {@link activeSectionId} measures by default, WIDENED to include the
 * receipt — `#guide-receipt` is a `.guide-colophon`, not a section, and the
 * rail's foot row jumps to it, so it must be able to become the active row.
 */
const CONTENTS_SPY_SELECTOR = '.guide-section[id], #guide-receipt';

/**
 * The single live contents-spy observer, module-level because only one guide
 * reader is ever mounted: a second arm supersedes the first rather than
 * running two spies over one rail.
 */
let contentsSpyObserver = null;

/**
 * The optimistically-applied rail click still in flight, or null. Held because
 * the observer's own boundary crossings land ~50ms before a smooth scroll
 * settles (measured 669ms vs 721ms), so a callback firing mid-scroll would
 * recompute the pre-landing geometry and re-assert the row the reader just
 * left; holding the clicked id for a short window lets the optimistic row
 * stand until the jump's own landing position confirms it.
 * @type {{id: string, at: number}|null}
 */
let contentsSpyPending = null;

/**
 * Paint one active row into a contents rail — the whole of the spy's write
 * side, pure and DOM-only so it is unit-testable without an observer and
 * reusable by the optimistic click path in {@link bindGuideToc}.
 *
 * Sets `aria-current="true"` on the row whose `data-guide-jump` matches
 * `activeId` and `"false"` on every other row, then moves the
 * `guide-contents-rail__group--live` class onto the group that holds the
 * active row (a flat solo rail has no groups, and the foot's receipt row
 * lives outside them, so either case simply leaves no group live).
 *
 * @param {Element|null} railEl - The `.guide-contents-rail` element
 * @param {string|null} activeId - Element id of the section the reader is in
 * @returns {void}
 */
export function applyGuideContentsActive(railEl, activeId) {
  if (!railEl?.querySelectorAll) return;

  let activeRow = null;
  railEl.querySelectorAll('.guide-contents-rail__row[data-guide-jump]').forEach((row) => {
    const isActive = !!activeId && row.dataset.guideJump === activeId;
    row.setAttribute('aria-current', isActive ? 'true' : 'false');
    if (isActive) activeRow = row;
  });

  const liveGroup = activeRow?.closest?.('.guide-contents-rail__group') || null;
  railEl.querySelectorAll('.guide-contents-rail__group').forEach((group) => {
    group.classList.toggle('guide-contents-rail__group--live', group === liveGroup);
  });
}

/**
 * Arm the ONE scroll-spy that drives the contents rail's active row, over the
 * guide body a host has just handed to the right panel. Returns a disposer.
 *
 * **Host-armed, never builder-armed.** A payload builder hands back a detached
 * node, so only the host knows when the panel has taken it.
 *
 * **Deliberately arms a node that may not be in the document yet.**
 * `right-panel:opened` fires BEFORE the cross-fade mounts the content on the
 * already-open swap branch and on drill, and no post-mount event exists — so
 * refusing a detached `contentEl` would mean /search and /discovery swap-opens
 * and every drill never arm at all. An IntersectionObserver accepts a detached
 * target and starts reporting once it is appended under the root, so the root
 * is resolved from the document (`.right-panel__content`, exactly as
 * {@link activeSectionId} resolves its scroller) rather than by walking up
 * from `contentEl`.
 *
 * It refuses — returning a no-op disposer, arming nothing — when there is no
 * `IntersectionObserver`, no `contentEl`, no rail inside it (the loading and
 * empty body branches emit none), or no `.right-panel__content` in the
 * document: with no root the observer would silently take the VIEWPORT as its
 * root and report nonsense.
 *
 * Every callback ignores the entries' own `isIntersecting` and recomputes
 * `activeSectionId(CONTENTS_SPY_SELECTOR)` — the boundaries are only a cue to
 * re-measure, so the rail and the header Contents menu agree on "where am I".
 *
 * @param {Element|null} contentEl - The panel content node holding the rail
 * @returns {() => void} Disposer; disconnects the observer, safe to call twice
 */
export function armGuideContentsSpy(contentEl) {
  // Single owner: whatever was armed before is superseded here, so there is
  // never more than one live observer no matter how many arm sites fire.
  contentsSpyObserver?.disconnect();
  contentsSpyObserver = null;

  const noop = () => {};
  if (typeof IntersectionObserver === 'undefined') return noop;
  if (!contentEl?.querySelector) return noop;
  if (!contentEl.querySelector('.guide-contents-rail')) return noop;
  const root = document.querySelector('.right-panel__content');
  if (!root) return noop;

  // Re-queried on every sync: the D12 You-part swap replaces the rail node
  // wholesale (`patchGuideContentsRail`), so a captured reference would paint
  // a detached rail between the patch and the host's re-arm.
  const sync = () => {
    const id = activeSectionId(CONTENTS_SPY_SELECTOR);
    if (contentsSpyPending && performance.now() - contentsSpyPending.at < 1500 && id !== contentsSpyPending.id) {
      return;
    }
    contentsSpyPending = null;
    applyGuideContentsActive(contentEl.querySelector('.guide-contents-rail'), id);
  };

  const observer = new IntersectionObserver(sync, {
    root,
    threshold: 0,
    rootMargin: '0px 0px -60% 0px'
  });
  contentEl.querySelectorAll(CONTENTS_SPY_SELECTOR).forEach(el => observer.observe(el));
  contentsSpyObserver = observer;
  sync();

  return () => {
    observer.disconnect();
    // Only clear the module slot if this observer is still the live one — a
    // stale disposer fired after a re-arm must not orphan the new spy.
    if (contentsSpyObserver === observer) contentsSpyObserver = null;
    contentsSpyPending = null;
  };
}

/**
 * Build the panel-header Contents menu for the merged reader: rows grouped by
 * part, plus one settings row pointing at the receipt when a receipt is
 * rendered (D11). `buildGuideContentsMenu(guide)` is untouched — off-Library
 * panels still call it with a single guide (D18).
 *
 * The button carries a visible "Contents" label and, when the couple's
 * reading is unread, a blue dot (D16); `RightPanelManager` reads both keys
 * individually, so no other consumer changes.
 *
 * Each group label carries its half's written date (D-K's parallel `meta`
 * map), because the rail is hidden at tiers B/C where this menu IS the map:
 * without it the per-half dates would vanish at narrow widths. A Together
 * half that is not readable still gets its label, with no rows beneath it
 * (D-M) — the rail's wordless ghost heading, mirrored, so a waiting or sealed
 * couple's half is mentioned somewhere above the trailer at every width.
 *
 * @param {Object} parts
 * @param {Object|null} parts.individual - Availability row, or the loaded UserGuide row
 * @param {Object|null} parts.shared - Shared-guide manifest item
 * @returns {Object|null} headerMenu config, or null when there is nothing to navigate between
 */
export function buildMergedGuideContentsMenu({ individual, shared } = {}) {
  const { you, together, receipt, meta } = contentsGroups({ individual, shared });
  const youRows = you || [];
  const togRows = together ? together.rows : [];

  // The ghost label is not a destination, so it does not count here: a menu
  // with nothing to press is still null.
  if (youRows.length + togRows.length + (receipt ? 1 : 0) < 2) return null;

  return {
    label: 'Contents',
    iconId: 'contents',
    menuClass: 'guide-contents-menu',
    showLabel: true,
    indicator: isTogetherUnread(shared),
    body: () => {
      const activeId = activeSectionId();
      const group = (label, rows, key, { date = '', ghost = false } = {}) => {
        if (rows.length === 0 && !ghost) return '';
        const dateHtml = ghost || !date ? '' : `<span class="guide-contents-menu__meta">${escapeHtml(date)}</span>`;
        // The ghost modifier hollows the swatch, exactly as the rail's ghost
        // heading does — one treatment for "this half exists and cannot be
        // opened", whichever map the reader is looking at.
        const ghostClass = ghost ? ' guide-contents-menu__group--ghost' : '';
        return `
        <p class="guide-contents-menu__group guide-contents-menu__group--${key}${ghostClass}"><span class="guide-contents-menu__swatch" aria-hidden="true"></span>${label}${dateHtml}</p>
        ${rows.map(({ label: rowLabel, num, id }) => `<button type="button" class="guide-contents-menu__row" data-guide-jump="${id}" aria-current="${String(id === activeId)}"><b>${String(num).padStart(2, '0')}</b><span>${escapeHtml(rowLabel)}</span></button>`).join('')}
      `;
      };

      return group('You', youRows, 'you', { date: meta?.you })
        + group('Together', togRows, 'together', { date: meta?.together, ghost: Boolean(together) && !together.readable })
        + (receipt
          ? `<button type="button" class="guide-contents-menu__row guide-contents-menu__row--settings" data-guide-jump="${GUIDE_RECEIPT_ID}"><b>·</b><span>Sharing settings</span></button>`
          : '');
    }
  };
}

/**
 * Whether the card this reader was opened from counts as Archived: every half
 * that is PRESENT is archived (D8). A half you don't have can't hold the card
 * in Archived, which is what lets a couple's reading that lands after you
 * archived your own resurface under Active.
 * @param {Object} parts
 * @returns {boolean}
 */
function isMergedArchived({ individual, shared }) {
  const halves = [];
  if (hasUserGuideRow(individual)) halves.push(!!individual.archivedAt || individual.archived === true);
  if (shared) halves.push(!!shared.archivedAt);
  return halves.length > 0 && halves.every(Boolean);
}

/**
 * The merged reader's header menu, or null when neither half exists yet.
 * @param {Object} parts
 * @param {Object|null} parts.individual - Availability row or `UserGuide` row
 * @param {Object|null} parts.shared - Shared-guide manifest item, if any
 * @param {Function} [parts.onOption] - Host handler for the Archive row
 * @returns {Object|null} `optionsDropdown` payload, or null to hide the menu
 */
function buildMergedOptionsDropdown({ individual, shared, onOption }) {
  if (!hasUserGuideRow(individual) && !shared) return null;
  return typeof onOption === 'function'
    ? buildGuideManagementDropdown(() => ({ individual, shared }), {
      archived: isMergedArchived({ individual, shared }),
      onOption
    })
    : buildGuideOptionsDropdown(() => ({ individual, shared }));
}

/**
 * Build the `right-panel:open` payload for the merged reader.
 *
 * One `contentType` (`library-guide`) for the whole surface: the shared
 * artifact no longer has a panel of its own. The star exists only when a
 * `UserGuide` row does (star acts on that row), so a Together-only card opens
 * with a kebab and no star. Copy/Share receive BOTH halves, each serialized
 * under its part label (D9).
 *
 * @param {Object} parts
 * @param {Object|null} parts.individual - Availability row, or the loaded UserGuide row
 * @param {Object|null} parts.shared - Shared-guide manifest item, or null when there is no couple
 * @param {string} [parts.partnerName] - Resolved partner display name
 * @param {(nextStarred: boolean) => boolean|Promise<boolean>} [parts.onStar] - Header star toggle
 * @param {(action: 'archive') => void} [parts.onOption] - Options-dropdown
 *   handler (Archive/Unarchive — no guide surface offers Delete). Omit it and
 *   the panel carries Copy/Share only.
 * @param {(nextEnabled: boolean) => Promise<boolean>} [parts.onToggleEnabled] - Sharing switch
 * @param {'you'|'together'|null} [parts.pendingPart] - Part a deprecated
 *   `?sharedGuide=` link asked for; forwarded to {@link renderMergedGuideBody},
 *   which stamps it on the root for the host to consume after mount (D10).
 * @returns {Object} right-panel payload
 */
export function buildMergedGuideDetailPayload({
  individual, shared, partnerName, onStar, onOption, onToggleEnabled, pendingPart = null
} = {}) {
  const content = document.createElement('div');
  content.innerHTML = renderMergedGuideBody({ individual, shared, partnerName, pendingPart });
  wireMergedGuideBody(content, { onToggleEnabled });

  const payload = {
    title: '',
    titleAlign: 'left',
    content,
    contentType: GUIDE_DETAIL_CONTENT_TYPE,
    headerMenu: buildMergedGuideContentsMenu({ individual, shared }),
    // The tier-A rail wants room for its own column; a generic panel-shell
    // affordance (RightPanelManager nudges to this width, never shrinking).
    preferredWidth: 880,
    // The management row (Archive/Unarchive) routes through the host, so it
    // appears only when the host supplied a handler; without one the reader
    // still gets Copy/Share rather than a menu whose row does nothing. A
    // reader with no row on either side (locked, or unlocked and never
    // written) gets no menu at all — there is nothing to copy, share, or
    // archive, the same rule the card's kebab applies.
    optionsDropdown: buildMergedOptionsDropdown({ individual, shared, onOption })
  };

  if (hasUserGuideRow(individual)) {
    payload.starButton = { starred: !!individual.starredAt, onClick: onStar };
  }

  return payload;
}
